import { Button, DatePicker, Input, Table, TableProps } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DoctorApi from "../../apis/doctorApi";

interface DataType {
  doctorId: string;
  name: string;
  jobTitle: string;
  skilledIllness: string;
  number: number;
  firstNumber: number;
  revisitNumber: number;
  thirdNumber: number;
  extraNumNumber: number;
}

const { RangePicker } = DatePicker;

const rangePresets: RangePickerProps["presets"] = [
  { label: "本月", value: [dayjs().startOf("month"), dayjs().endOf("month")] },
  {
    label: "上月",
    value: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
  {
    label: "近三月",
    value: [dayjs().subtract(2, "month").startOf("month"), dayjs().endOf("month")],
  },
  {
    label: "近半年",
    value: [dayjs().subtract(5, "month").startOf("month"), dayjs().endOf("month")],
  },
  { label: "今年", value: [dayjs().startOf("year"), dayjs().endOf("year")] },
];

const Doctor: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataType[]>([]);
  // 医生名称
  const [doctorName, setDoctorName] = useState<string>('');
  // 页码
  const [pageNumber, setPageNumber] = useState<number>(1);
  // 数据总数
  const [total, setTotal] = useState<number>(0);
  // 时间范围，默认当前月
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "姓名",
      dataIndex: "name",
      key: "name",
      align: "center" as const,
    },
    {
      title: "职称",
      dataIndex: "jobTitle",
      key: "jobTitle",
      align: "center" as const,
    },
    {
      title: "擅长疾病",
      dataIndex: "skilledIllness",
      key: "skilledIllness",
      align: "center" as const,
    },
    {
      title: "问诊次数",
      dataIndex: "number",
      key: "number",
      align: "center" as const,
    },
    {
      title: "初诊次数",
      dataIndex: "firstNumber",
      key: "firstNumber",
      align: "center" as const,
    },
    {
      title: "复诊次数",
      dataIndex: "revisitNumber",
      key: "revisitNumber",
      align: "center" as const,
    },
    {
      title: "三诊次数",
      dataIndex: "thirdNumber",
      key: "thirdNumber",
      align: "center" as const,
    },
    {
      title: "三诊以上",
      dataIndex: "extraNumNumber",
      key: "extraNumNumber",
      align: "center" as const,
    },
    {
      title:"操作",
      key:"操作",
      align: "center" as const,
      render: (_, re) => (
        <Button type="link" onClick={() => navigate("/home/doctor-details/" + re.doctorId)}>
          查看
        </Button>
      ),
    },
  ];

  useEffect(() => {
    getDoctorList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 医生列表
  const getDoctorList = (number: number = 0) => {
    const startTime = dateRange[0].format("YYYY-MM-DD") + " 00:00:00";
    const endTime = dateRange[1].format("YYYY-MM-DD") + " 23:59:59";
    DoctorApi.getDoctorList(number ?? pageNumber, doctorName, startTime, endTime).then((value) => {
      setTotal(value.data["total"]);
      setData(value.data["list"]);
      setLoading(false);
    });
  };

  return (
    <div className="Doctor pageItem">
      <h1>医生</h1>
      <div
        className="flexBlockCenter"
        style={{ marginBottom: "20px", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}
      >
        <div className="flexBlockCenter" style={{ gap: "16px", flexWrap: "wrap" }}>
          <h4 className="flexBlockCenter" style={{ margin: 0 }}>
            医生：
            <Input
              placeholder="请输入医生姓名"
              allowClear
              onChange={(e) => setDoctorName(e.target.value)}
            />
          </h4>
          <h4 className="flexBlockCenter" style={{ margin: 0 }}>
            时间：
            <RangePicker
              value={dateRange}
              presets={rangePresets}
              onChange={(values) => {
                if (values && values[0] && values[1]) {
                  setDateRange([values[0], values[1]]);
                }
              }}
            />
          </h4>
        </div>

        <Button type="primary" onClick={() => getDoctorList()}>
          查询
        </Button>
      </div>

      <Table<DataType>
        loading={loading}
        rowKey="doctorId"
        columns={columns}
        dataSource={data}
        pagination={{
          pageSize: 10,
          total: total,
          showSizeChanger: false,
          onChange: (number) => {
            setPageNumber(number);
            getDoctorList(number);
          },
        }}
      />
    </div>
  );
};

export default Doctor;
